import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text
} from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useInvalidateDeploymentList,
  useStartDeployment,
  useStopDeployment
} from '@/queries/deploymentQueries'
import { showToast } from '@/theme/notifications'

type DeploymentPauseConfirmProps = {
  opened: boolean
  deploymentId: string
  isPaused: boolean
  onClose: () => void
}

export const DeploymentPauseConfirm = ({
  opened,
  deploymentId,
  isPaused,
  onClose
}: DeploymentPauseConfirmProps) => {
  const intl = useIntl()

  const { mutateAsync: stopDeployment, isPending: isPausing } =
    useStopDeployment()

  const { mutateAsync: startDeployment, isPending: isResuming } =
    useStartDeployment()

  const { invalidateDeploymentList } = useInvalidateDeploymentList()

  const handlePauseDeployment = async () => {
    try {
      await stopDeployment(deploymentId)

      showToast(
        intl.formatMessage({ id: 'deployments.pause.inProgress' }),
        'info'
      )

      void invalidateDeploymentList()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'deployments.pause.error' })
        ),
        'error'
      )
    }
  }

  const handleResumeDeployment = async () => {
    try {
      await startDeployment(deploymentId)

      showToast(
        intl.formatMessage({ id: 'deployments.resume.inProgress' }),
        'info'
      )

      void invalidateDeploymentList()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'deployments.resume.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={
        isPaused ? (
          <FormattedMessage id="deployments.resume" />
        ) : (
          <FormattedMessage id="deployments.pause" />
        )
      }
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPausing || isResuming} />

        <Stack gap="xl">
          <Text size="sm">
            {isPaused ? (
              <FormattedMessage id="deployments.resume.confirmation" />
            ) : (
              <FormattedMessage id="deployments.pause.confirmation" />
            )}
          </Text>

          <Group justify="end">
            <Button miw={120} variant="default" onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>

            <Button
              miw={120}
              onClick={() =>
                void (isPaused
                  ? handleResumeDeployment()
                  : handlePauseDeployment())
              }
            >
              {isPaused ? (
                <FormattedMessage id="deployments.resume" />
              ) : (
                <FormattedMessage id="deployments.pause" />
              )}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
